import type { PaymentMethod } from '@contracts/payment/PaymentMethod';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import { Banner } from '@src/common/components/Banner';
import { Divider } from '@src/common/components/Divider';
import { Stack } from '@src/common/components/Stack';
import { Typography } from '@src/common/components/Typography';
import { css } from '@src/common/styles/Styles';
import { theme } from '@src/common/styles/Theme';
import Config from '@src/config/Config';
import { t } from 'i18next';
import { Trans } from 'react-i18next';

export type PaymentMethodBannerProps = {
  isClientUser: boolean;
  paymentMethod: PaymentMethod;
  hasCreditCard: boolean;
};

export const PaymentMethodBanner = (props: PaymentMethodBannerProps) => {
  const message = getPaymentBannerMessage({
    isClientUser: props.isClientUser,
    paymentMethod: props.paymentMethod,
    hasSavedCreditCards: props.hasCreditCard,
  });

  return (
    <Stack direction="column" gap={theme.spacing['3']}>
      <Stack direction="column" gap={theme.spacing['3']}>
        <Typography className={title}>{t('paymentMethod.title')}</Typography>
        {message && (
          <Typography>
            <Trans
              i18nKey={message}
              values={{
                brand: Config.VITE_BRAND,
                phone: Config.VITE_BRAND_PHONE,
                mail: Config.VITE_BRAND_EMAIL,
                webForm: Config.VITE_BRAND_WEBFORM,
              }}
              components={{
                mail: <a href={`mailto:${Config.VITE_BRAND_EMAIL}`} />,
                phone: <a href={`tel:${Config.VITE_BRAND_PHONE}`} />,
                webForm: (
                  <a
                    href={Config.VITE_BRAND_WEBFORM}
                    target="_blank"
                    rel="noopener noreferrer"
                  />
                ),
                strong: <strong style={{ fontWeight: 600 }} />,
              }}
            />
          </Typography>
        )}
      </Stack>
      <Divider />
      <Banner
        severity="other"
        style={{
          width: '100%',
          paddingTop: theme.spacing['8'],
          paddingBottom: theme.spacing['8'],
        }}
      >
        <Stack direction="row" alignItems="center" gap={theme.spacing['2']}>
          <ReceiptLongOutlinedIcon />
          <Typography>
            {t(`paymentMethod.${props.paymentMethod.toLowerCase()}`)}
          </Typography>
        </Stack>
      </Banner>
    </Stack>
  );
};

interface GetPaymentBannerMessageParams {
  isClientUser: boolean;
  paymentMethod?: PaymentMethod;
  hasSavedCreditCards: boolean;
}

const getPaymentBannerMessage = ({
  isClientUser,
  paymentMethod,
  hasSavedCreditCards,
}: GetPaymentBannerMessageParams): string | undefined => {
  if (isClientUser) {
    if (paymentMethod === 'CREDIT_CARD' && !hasSavedCreditCards) {
      return 'paymentMethod.no_credit_card_client_message';
    }
    return 'paymentMethod.general_client_message';
  }

  if (paymentMethod === 'CREDIT_CARD' && !hasSavedCreditCards) {
    return 'paymentMethod.no_credit_card_agent_message';
  }

  return undefined;
};

const title = css(() => ({
  fontWeight: 500,
}));
